import { marked } from "marked";

import { convertAmbifiMarkdownColorSyntaxToHtml } from "./ambifiMarkdownColorUtils";

function escapeAttributeValue(value = "") {
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

const CSS_UNITLESS_NUMBER_PROPS = new Set([
	"flex",
	"flex-grow",
	"flex-shrink",
	"font-weight",
	"opacity",
	"order",
	"z-index",
	"zoom"
]);

const STYLE_PROP_ALIASES = {
	resizeMode: "objectFit",
	textDecorationLine: "textDecoration"
};

function isPlainObject(value) {
	return Object.prototype.toString.call(value) === "[object Object]";
}

function toKebabCase(value = "") {
	return String(value).replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}

function normalizeCssStyleValue(styleKey, value) {
	if (value === null || typeof value === "undefined" || value === "") {
		return null;
	}
	if (isPlainObject(value) || Array.isArray(value)) {
		return null;
	}
	if (styleKey === "resizeMode") {
		if (value === "stretch") {
			return "fill";
		}
		if (value === "center") {
			return "none";
		}
	}
	const cssPropName = toKebabCase(STYLE_PROP_ALIASES[styleKey] || styleKey);
	if (typeof value === "number" && !CSS_UNITLESS_NUMBER_PROPS.has(cssPropName)) {
		return `${value}px`;
	}
	return String(value);
}

function buildStyleAttributeFromObject(style = {}) {
	if (!isPlainObject(style)) {
		return "";
	}
	const declarations = [];
	Object.keys(style).forEach((styleKey) => {
		const cssValue = normalizeCssStyleValue(styleKey, style[styleKey]);
		if (cssValue === null) {
			return;
		}
		const cssPropName = toKebabCase(STYLE_PROP_ALIASES[styleKey] || styleKey);
		declarations.push(`${cssPropName}:${cssValue}`);
	});
	return declarations.length > 0 ? ` style="${escapeAttributeValue(declarations.join(";"))}"` : "";
}

function mergeMarkdownStyle(markdownStyles = {}, styleKeys = [], baseStyle = {}) {
	const merged = isPlainObject(baseStyle) ? { ...baseStyle } : {};
	styleKeys.forEach((styleKey) => {
		if (isPlainObject(markdownStyles[styleKey])) {
			Object.assign(merged, markdownStyles[styleKey]);
		}
	});
	return merged;
}

function getMarkdownStyleAttribute(markdownStyles = {}, styleKeys = [], baseStyle = {}) {
	return buildStyleAttributeFromObject(mergeMarkdownStyle(markdownStyles, styleKeys, baseStyle));
}

function getBlockquoteStyleAttribute(markdownStyles = {}) {
	const blockquoteStyle = mergeMarkdownStyle(markdownStyles, ["blockquote"]);

	if (blockquoteStyle.borderStyle && !blockquoteStyle.borderLeftStyle) {
		blockquoteStyle.borderLeftStyle = blockquoteStyle.borderStyle;
	}

	delete blockquoteStyle.borderStyle;

	return buildStyleAttributeFromObject(blockquoteStyle);
}

export function markdownToHtml(label, params = {}, wrap = true) {
	const safeLabel = convertAmbifiMarkdownColorSyntaxToHtml(label === null || typeof label === "undefined" ? "" : `${label}`);
	const markdownStyleOverrides = isPlainObject(params.markdownStyles) ? params.markdownStyles : {};
	let myRenderer = new marked.Renderer();
	myRenderer.link = function (href, title, text) {
		const safeHref = typeof href === "string" ? href : "";
		const safeTitle = typeof title === "string" ? title : "";
		const safeText = text === null || typeof text === "undefined" ? "" : `${text}`;

		if (!params.readOnlyLinks) {
			let external, newWindow, out;
			external = /^((https?|facetime):\/\/|(sms|tel|mailto|skype):).+$/.test(safeHref);
			newWindow = external || safeTitle === "newWindow";

			//console.log(`href: ${href}, title: ${title}, text: ${text}`);

			if (safeHref.toLowerCase().startsWith("goto:") ||
				safeHref.toLowerCase().startsWith("newsessionandgoto:") ||
				safeHref.toLowerCase().startsWith("gotoandreturn:") ||
				safeHref.toLowerCase().startsWith("newsessionandgotoandreturn:") ||
				safeHref.toLowerCase().startsWith("gotoandopenbirdseyeview:") ||
				safeHref.toLowerCase().startsWith("resetcurrentactivity") ||
				safeHref.toLowerCase().startsWith("resetcurrentlist") ||
				safeHref.toLowerCase().startsWith("resetcurrentsection") ||
				safeHref.toLowerCase().startsWith("resetrangeandgoto:") ||
				safeHref.toLowerCase().startsWith("checkrangeandgoto:") ||
				safeHref.toLowerCase().startsWith("opencomments") ||
				safeHref.toLowerCase().startsWith("opennotes") ||
				safeHref.toLowerCase().startsWith("openmedia") ||
				safeHref.toLowerCase().startsWith("opensketchpad")) {
				out = `<a onClick="event.stopPropagation(); playerActionsInlineLinkUrl('${safeHref}',${params.listIndex}, ${params.sectionIndex}, ${params.itemIndex});" href='#' `;
			} else {
				out = "<a href=\"" + safeHref + "\"";
			}

			if (newWindow) {
				out += " target=\"_blank\"";
			}
			out += getMarkdownStyleAttribute(markdownStyleOverrides, ["link"]);
			if (safeTitle && safeTitle !== "newWindow") {
				out += " title=\"" + escapeAttributeValue(safeTitle) + "\"";
			}

			return out += ">" + safeText + "</a>";
		} else {
			let out = "<a href=\"javascript:void(0)\"";
			out += getMarkdownStyleAttribute(markdownStyleOverrides, ["link"]);
			if (safeTitle) {
				out += " title=\"" + escapeAttributeValue(safeTitle) + "\"";
			}
			return out += ">" + safeText + "</a>";
		}
	};

	myRenderer.image = function (href, title, text) {
		const safeHref = href === null || typeof href === "undefined" ? "" : `${href}`;
		const safeTitle = title === null || typeof title === "undefined" ? "" : `${title}`;
		const safeText = text === null || typeof text === "undefined" ? "" : `${text}`;
		const imageStyleAttr = getMarkdownStyleAttribute(markdownStyleOverrides, ["image"], { maxWidth: "100%" });
		const titleAttr = safeTitle ? ` title="${escapeAttributeValue(safeTitle)}"` : "";
		const altAttr = safeText ? ` alt="${escapeAttributeValue(safeText)}"` : "";
		return `<img${imageStyleAttr} src="${escapeAttributeValue(safeHref)}"${titleAttr}${altAttr} /><br/><div>${safeText}</div>`;
	};

	myRenderer.code = function (code, language) {
		const languageClass = language ? ` class="language-${escapeAttributeValue(language)}"` : "";
		const codeBlockStyleAttr = getMarkdownStyleAttribute(markdownStyleOverrides, ["codeBlock"]);
		return `<pre${codeBlockStyleAttr}><code${languageClass}>${escapeAttributeValue(code)}</code></pre>\n`;
	};

	myRenderer.codespan = function (text) {
		return `<code${getMarkdownStyleAttribute(markdownStyleOverrides, ["codeInline", "inlineCode"])}>${escapeAttributeValue(text)}</code>`;
	};

	myRenderer.blockquote = function (quote) {
		return `<blockquote${getBlockquoteStyleAttribute(markdownStyleOverrides)}>${quote}</blockquote>\n`;
	};

	myRenderer.heading = function (text, level) {
		return `<h${level}${getMarkdownStyleAttribute(markdownStyleOverrides, ["heading", `heading${level}`])}>${text}</h${level}>\n`;
	};

	myRenderer.paragraph = function (text) {
		return `<p${getMarkdownStyleAttribute(markdownStyleOverrides, ["paragraph"])}>${text}</p>\n`;
	};

	myRenderer.list = function (body, ordered, start) {
		const type = ordered ? "ol" : "ul";
		const startAttr = ordered && typeof start === "number" && start !== 1 ? ` start="${start}"` : "";
		const styleKeys = ordered ? ["list", "listOrdered"] : ["list", "listUnordered"];
		return `<${type}${startAttr}${getMarkdownStyleAttribute(markdownStyleOverrides, styleKeys)}>\n${body}</${type}>\n`;
	};

	myRenderer.listitem = function (text) {
		return `<li${getMarkdownStyleAttribute(markdownStyleOverrides, ["listItem"])}>${text}</li>\n`;
	};

	myRenderer.strong = function (text) {
		return `<strong${getMarkdownStyleAttribute(markdownStyleOverrides, ["strong"])}>${text}</strong>`;
	};

	myRenderer.em = function (text) {
		return `<em${getMarkdownStyleAttribute(markdownStyleOverrides, ["em"])}>${text}</em>`;
	};

	myRenderer.del = function (text) {
		return `<del${getMarkdownStyleAttribute(markdownStyleOverrides, ["del", "strikethrough"])}>${text}</del>`;
	};

	myRenderer.table = function (header, body) {
		return `<table${getMarkdownStyleAttribute(markdownStyleOverrides, ["table"])}>\n<thead${getMarkdownStyleAttribute(markdownStyleOverrides, ["tableHeader"])}>\n${header}</thead>\n<tbody>\n${body}</tbody>\n</table>\n`;
	};

	myRenderer.tablerow = function (content) {
		return `<tr${getMarkdownStyleAttribute(markdownStyleOverrides, ["tableRow"])}>\n${content}</tr>\n`;
	};

	myRenderer.tablecell = function (content, flags) {
		const type = flags.header ? "th" : "td";
		const alignStyle = flags.align ? { textAlign: flags.align } : {};
		const styleKeys = flags.header ? ["tableHeaderCell"] : ["tableRowCell"];
		return `<${type}${getMarkdownStyleAttribute(markdownStyleOverrides, styleKeys, alignStyle)}>${content}</${type}>\n`;
	};

	const html = marked.parse(safeLabel, {
		renderer: myRenderer,
		gfm: true,
		tables: true,
		breaks: true,
		pedantic: true,
		sanitize: false,
		smartLists: true,
		smartypants: false
	});

	return wrap ? { __html: html } : html;
}
